import { cn } from "@wags/ui/ui-utils";
import { getProviderType, ProviderType } from "@wags/catalog/pure.ts";
import type { ReactNode } from "react";
import type { ProviderProjection } from "../provider.contract";
import { toPolicyViews } from "./provider-policies";
import { PoliciesColumn } from "./provider-policies-column";
import { toProviderSocialLinks } from "./provider-social-links";
import { SocialLinksColumn } from "./provider-social-links-column";

type BandColumn = {
    title: string;
    labelsItself?: boolean;
    content: ReactNode;
};

export function ProviderDetailsSection({
    provider,
}: {
    provider:
        | Pick<ProviderProjection, "policies" | "socialUrls" | "type">
        | undefined;
}) {
    if (!provider) return null;
    const socialLinks = toProviderSocialLinks(provider.socialUrls);
    const columns: BandColumn[] = [];
    if (provider.policies.length > 0) {
        const isBreeder =
            getProviderType(provider.type) === ProviderType.Breeder;
        columns.push({
            title: "Policies",
            labelsItself: true,
            content: (
                <PoliciesColumn
                    policies={toPolicyViews(provider.policies, isBreeder)}
                />
            ),
        });
    }
    if (socialLinks.length > 0) {
        columns.push({
            title: "Find us online",
            content: <SocialLinksColumn links={socialLinks} />,
        });
    }
    const [firstColumn] = columns;
    if (!firstColumn) return null;
    const isSingleColumn = columns.length === 1;
    return (
        <section className="mt-10 border-t border-doberman-black/10 pt-10 lg:flex lg:items-start lg:gap-x-16">
            <h2 className="text-section-title lg:w-70 lg:shrink-0 text-doberman-black text-balance">
                {isSingleColumn ? firstColumn.title : "Things to know"}
            </h2>
            <div
                className={cn(
                    "mt-5 grid min-w-0 gap-8 md:items-start lg:mt-0 lg:max-w-190 lg:flex-1",
                    !isSingleColumn && "md:grid-cols-2",
                )}
            >
                {columns.map(({ title, labelsItself, content }) => (
                    <div key={title} className="min-w-0">
                        {!isSingleColumn && !labelsItself && (
                            <h3 className="mb-3 text-body-small font-medium text-doberman-black/55">
                                {title}
                            </h3>
                        )}
                        {content}
                    </div>
                ))}
            </div>
        </section>
    );
}
